// Dictionary, part 2 — the PO1 (baseline item) loop, its sub-loops (SLN
// sub-line, TC1 transportation, and a nested N1 Name sub-loop), and the
// transaction summary (CTT/AMT). Generic X12 850 (5010) definitions only.
//
// REF, PER, CTP, TAX, FOB, DTM, LDT, ISS, PCT, MSG, LM, LQ, PWK and N1-N4 all
// recur inside this loop too, but X12 defines one segment per id — they're
// already defined in dictionary/header-segments.ts and merged in by index.ts,
// so they are not redefined here.

import {LoopId, type ElementDefinition, type SegmentDefinition} from './types';
import {PRODUCT_SERVICE_ID_QUALIFIER_CODES} from './product-service-id-qualifiers';

function el(
  position: number,
  label: string,
  description: string,
  extra: Partial<ElementDefinition> = {}
): ElementDefinition {
  return {position, label, description, ...extra};
}

export const ITEM_LOOP_SEGMENTS: Record<string, SegmentDefinition> = {
  PO1: {
    id: 'PO1',
    name: 'Baseline Item Data',
    purpose:
      'Opens one purchase order line: the line number, quantity, unit price and the item being ordered. Every item loop starts with a PO1.',
    loop: LoopId.Item,
    required: true,
    elements: [
      el(
        1,
        'Assigned Identification',
        'The line number, assigned by the buyer.'
      ),
      el(2, 'Quantity Ordered', 'How many units are being ordered.', {
        dataType: 'numeric',
        required: true,
      }),
      el(
        3,
        'Unit or Basis for Measurement Code',
        'The unit the quantity is expressed in.',
        {
          dataType: 'code',
          required: true,
          codes: [
            {code: 'EA', label: 'Each'},
            {code: 'CA', label: 'Case'},
            {code: 'DZ', label: 'Dozen'},
            {code: 'BX', label: 'Box'},
            {code: 'CT', label: 'Carton'},
            {code: 'LB', label: 'Pound'},
            {code: 'FT', label: 'Foot'},
          ],
        }
      ),
      el(4, 'Unit Price', 'The price per unit.', {
        dataType: 'numeric',
        required: true,
      }),
      el(5, 'Basis of Unit Price Code', 'What the unit price is quoted per.', {
        dataType: 'code',
        codes: [
          {code: 'PE', label: 'Price per Each'},
          {code: 'CT', label: 'Price per Hundred'},
        ],
      }),
      el(
        6,
        'Product/Service ID Qualifier',
        'What kind of item identifier follows.',
        {
          dataType: 'code',
          codes: PRODUCT_SERVICE_ID_QUALIFIER_CODES,
        }
      ),
      el(
        7,
        'Product/Service ID',
        'The item identifier, in the scheme named by element 6.'
      ),
      el(
        8,
        'Product/Service ID Qualifier',
        'A second item identifier scheme, when the item carries more than one id.',
        {
          dataType: 'code',
          codes: PRODUCT_SERVICE_ID_QUALIFIER_CODES,
        }
      ),
      el(9, 'Product/Service ID', 'The second item identifier.'),
      el(
        10,
        'Product/Service ID Qualifier',
        'A subsequent item identifier scheme, when the item carries more than two ids.',
        {dataType: 'code', codes: PRODUCT_SERVICE_ID_QUALIFIER_CODES}
      ),
      el(
        12,
        'Product/Service ID Qualifier',
        'A subsequent item identifier scheme, when the item carries more than two ids.',
        {dataType: 'code', codes: PRODUCT_SERVICE_ID_QUALIFIER_CODES}
      ),
      el(
        14,
        'Product/Service ID Qualifier',
        'A subsequent item identifier scheme, when the item carries more than two ids.',
        {dataType: 'code', codes: PRODUCT_SERVICE_ID_QUALIFIER_CODES}
      ),
      el(
        16,
        'Product/Service ID Qualifier',
        'A subsequent item identifier scheme, when the item carries more than two ids.',
        {dataType: 'code', codes: PRODUCT_SERVICE_ID_QUALIFIER_CODES}
      ),
      el(
        18,
        'Product/Service ID Qualifier',
        'A subsequent item identifier scheme, when the item carries more than two ids.',
        {dataType: 'code', codes: PRODUCT_SERVICE_ID_QUALIFIER_CODES}
      ),
      el(
        20,
        'Product/Service ID Qualifier',
        'A subsequent item identifier scheme, when the item carries more than two ids.',
        {dataType: 'code', codes: PRODUCT_SERVICE_ID_QUALIFIER_CODES}
      ),
      el(
        22,
        'Product/Service ID Qualifier',
        'A subsequent item identifier scheme, when the item carries more than two ids.',
        {dataType: 'code', codes: PRODUCT_SERVICE_ID_QUALIFIER_CODES}
      ),
      el(
        24,
        'Product/Service ID Qualifier',
        'A subsequent item identifier scheme, when the item carries more than two ids.',
        {dataType: 'code', codes: PRODUCT_SERVICE_ID_QUALIFIER_CODES}
      ),
    ],
  },

  MEA: {
    id: 'MEA',
    name: 'Measurements',
    purpose:
      'Records a physical measurement of the item — weight, dimension, volume.',
    loop: LoopId.Item,
    elements: [
      el(
        1,
        'Measurement Reference ID Code',
        'What the measurement is used for.',
        {
          dataType: 'code',
          codes: [
            {code: 'PD', label: 'Physical Dimensions'},
            {code: 'WT', label: 'Weight'},
          ],
        }
      ),
      el(2, 'Measurement Qualifier', 'Which dimension this measurement is.', {
        dataType: 'code',
        codes: [
          {code: 'LN', label: 'Length'},
          {code: 'WD', label: 'Width'},
          {code: 'HT', label: 'Height'},
          {code: 'WT', label: 'Weight'},
          {code: 'VOL', label: 'Volume'},
          {code: 'ZZ', label: 'Mutually Defined'},
        ],
      }),
      el(3, 'Measurement Value', 'The measured value.', {dataType: 'numeric'}),
      el(
        4,
        'Unit or Basis for Measurement Code',
        'The unit the value is expressed in.',
        {dataType: 'code'}
      ),
    ],
  },

  PID: {
    id: 'PID',
    name: 'Product/Item Description',
    purpose: 'A free-form or coded description of the item.',
    loop: LoopId.Item,
    elements: [
      el(
        1,
        'Item Description Type',
        'Whether the description is free-form or a coded value.',
        {
          dataType: 'code',
          required: true,
          codes: [
            {code: 'F', label: 'Free-form'},
            {code: 'S', label: 'Structured (Code)'},
          ],
        }
      ),
      el(
        2,
        'Product/Process Characteristic Code',
        'Which characteristic is being described, for a structured description.',
        {
          dataType: 'code',
        }
      ),
      el(
        3,
        'Agency Qualifier Code',
        'The agency that maintains the coded value used.',
        {dataType: 'code'}
      ),
      el(4, 'Product Description Code', 'The coded description value.'),
      el(5, 'Description', 'The description text itself.'),
    ],
  },

  PKG: {
    id: 'PKG',
    name: 'Marking, Packaging, Loading',
    purpose: 'Describes how the item is packaged, marked or loaded.',
    loop: LoopId.Item,
    elements: [
      el(
        1,
        'Item Description Type',
        'Whether the description is free-form or a coded value.',
        {
          dataType: 'code',
          codes: [
            {code: 'F', label: 'Free-form'},
            {code: 'S', label: 'Structured (Code)'},
          ],
        }
      ),
      el(
        2,
        'Packaging Characteristic Code',
        'What aspect of packaging this describes.',
        {dataType: 'code'}
      ),
      el(3, 'Packaging Description Code', 'The coded packaging value.'),
      el(4, 'Description', 'Free-form packaging instructions.'),
    ],
  },

  PO3: {
    id: 'PO3',
    name: 'Additional Item Detail',
    purpose:
      'Carries an additional price/quantity detail for the line — e.g. a price change or a secondary quantity.',
    loop: LoopId.Item,
    elements: [
      el(
        1,
        'Change/Add/Delete Code',
        'What kind of change this detail represents.',
        {dataType: 'code'}
      ),
      el(2, 'Quantity', 'The quantity for this detail.', {dataType: 'numeric'}),
      el(
        3,
        'Unit or Basis for Measurement Code',
        'The unit the quantity is expressed in.',
        {dataType: 'code'}
      ),
      el(4, 'Unit Price', 'The price for this detail.', {dataType: 'numeric'}),
      el(5, 'Basis of Unit Price Code', 'What the unit price is quoted per.', {
        dataType: 'code',
      }),
      el(
        6,
        'Product/Service ID Qualifier',
        'What kind of item identifier follows.',
        {dataType: 'code'}
      ),
      el(7, 'Product/Service ID', 'The item identifier for this detail.'),
    ],
  },

  SAC: {
    id: 'SAC',
    name: 'Service, Promotion, Allowance, or Charge Information',
    purpose:
      'Applies an allowance or charge (e.g. a freight charge, a promotional discount) to the line or the order.',
    loop: LoopId.Item,
    elements: [
      el(
        1,
        'Allowance or Charge Indicator',
        'Whether this reduces or adds to the amount due.',
        {
          dataType: 'code',
          required: true,
          codes: [
            {code: 'A', label: 'Allowance'},
            {code: 'C', label: 'Charge'},
          ],
        }
      ),
      el(
        2,
        'Service, Promotion, Allowance, or Charge Code',
        'What the allowance/charge is for, e.g. freight.',
        {
          dataType: 'code',
        }
      ),
      el(5, 'Amount', 'The allowance/charge amount.', {dataType: 'numeric'}),
      el(
        7,
        'Percent',
        'The allowance/charge expressed as a percentage, if applicable.',
        {dataType: 'numeric'}
      ),
      el(13, 'Description', 'Free-form description of the allowance/charge.'),
    ],
  },

  IT8: {
    id: 'IT8',
    name: 'Conditions of Sale',
    purpose:
      'States payment terms specific to this line, when they differ from the order-level ITD.',
    loop: LoopId.Item,
    elements: [
      el(
        1,
        'Terms Basis Days Due',
        'Days from the basis date terms are measured against.',
        {dataType: 'numeric'}
      ),
      el(2, 'Terms Net Days', 'Days from the basis date full payment is due.', {
        dataType: 'numeric',
      }),
      el(3, 'Terms Discount Percent', 'Early-payment discount percentage.', {
        dataType: 'numeric',
      }),
    ],
  },

  CTB: {
    id: 'CTB',
    name: 'Restrictions/Conditions',
    purpose:
      'States a restriction or condition that applies to the line, e.g. a substitution restriction.',
    loop: LoopId.Item,
    elements: [
      el(
        1,
        'Agency Qualifier Code',
        'The agency that defines the restriction/condition code used.',
        {dataType: 'code'}
      ),
      el(2, 'Restriction/Condition Code', 'The coded restriction/condition.', {
        dataType: 'code',
      }),
      el(
        3,
        'Restriction/Condition Value',
        'The value the restriction/condition applies, if any.'
      ),
    ],
  },

  SDQ: {
    id: 'SDQ',
    name: 'Destination Quantity',
    purpose: 'Splits the line quantity across multiple ship-to destinations.',
    loop: LoopId.Item,
    elements: [
      el(
        1,
        'Unit or Basis for Measurement Code',
        'The unit the quantities are expressed in.',
        {
          dataType: 'code',
          required: true,
        }
      ),
      el(
        2,
        'Identification Code Qualifier',
        'How the destination location id is assigned.',
        {dataType: 'code'}
      ),
      el(3, 'Identification Code', 'The destination location id.'),
      el(4, 'Quantity', 'The quantity going to that destination.', {
        dataType: 'numeric',
      }),
    ],
  },

  PAM: {
    id: 'PAM',
    name: 'Period Amount',
    purpose:
      'Ties a quantity or amount on the line to a time period, e.g. a scheduled release quantity.',
    loop: LoopId.Item,
    elements: [
      el(1, 'Time Period Qualifier', 'What period this amount covers.', {
        dataType: 'code',
      }),
      el(2, 'Number of Periods', 'How many periods this covers.', {
        dataType: 'numeric',
      }),
      el(3, 'Monetary Amount', 'The amount for the period.', {
        dataType: 'numeric',
      }),
    ],
  },

  SLN: {
    id: 'SLN',
    name: 'Subline Item Detail',
    purpose:
      "Describes a component or sub-item of the PO1 line — e.g. one item in a kit. Opens the line's sub-line loop.",
    loop: LoopId.SubLine,
    elements: [
      el(1, 'Assigned Identification', 'The sub-line number.', {
        required: true,
      }),
      el(
        2,
        'Assigned Identification',
        "The parent PO1 line's number this sub-line belongs to."
      ),
      el(
        3,
        'Relationship Code',
        'How this sub-line relates to the parent line.',
        {
          dataType: 'code',
          codes: [
            {code: 'A', label: 'Add'},
            {code: 'D', label: 'Delete'},
            {code: 'C', label: 'Change'},
          ],
        }
      ),
      el(4, 'Quantity', 'The sub-line quantity.', {dataType: 'numeric'}),
      el(
        5,
        'Unit or Basis for Measurement Code',
        'The unit the sub-line quantity is expressed in.',
        {
          dataType: 'code',
        }
      ),
      el(7, 'Unit Price', 'The sub-line unit price.', {dataType: 'numeric'}),
      el(
        8,
        'Basis of Unit Price Code',
        'What the sub-line unit price is quoted per.',
        {dataType: 'code'}
      ),
      el(
        9,
        'Product/Service ID Qualifier',
        'What kind of item identifier follows, for this sub-line.',
        {dataType: 'code', codes: PRODUCT_SERVICE_ID_QUALIFIER_CODES}
      ),
      el(
        11,
        'Product/Service ID Qualifier',
        'What kind of item identifier follows, for this sub-line.',
        {dataType: 'code', codes: PRODUCT_SERVICE_ID_QUALIFIER_CODES}
      ),
      el(
        13,
        'Product/Service ID Qualifier',
        'What kind of item identifier follows, for this sub-line.',
        {dataType: 'code', codes: PRODUCT_SERVICE_ID_QUALIFIER_CODES}
      ),
      el(
        15,
        'Product/Service ID Qualifier',
        'What kind of item identifier follows, for this sub-line.',
        {dataType: 'code', codes: PRODUCT_SERVICE_ID_QUALIFIER_CODES}
      ),
      el(
        17,
        'Product/Service ID Qualifier',
        'What kind of item identifier follows, for this sub-line.',
        {dataType: 'code', codes: PRODUCT_SERVICE_ID_QUALIFIER_CODES}
      ),
      el(
        19,
        'Product/Service ID Qualifier',
        'What kind of item identifier follows, for this sub-line.',
        {dataType: 'code', codes: PRODUCT_SERVICE_ID_QUALIFIER_CODES}
      ),
      el(
        21,
        'Product/Service ID Qualifier',
        'What kind of item identifier follows, for this sub-line.',
        {dataType: 'code', codes: PRODUCT_SERVICE_ID_QUALIFIER_CODES}
      ),
      el(
        23,
        'Product/Service ID Qualifier',
        'What kind of item identifier follows, for this sub-line.',
        {dataType: 'code', codes: PRODUCT_SERVICE_ID_QUALIFIER_CODES}
      ),
      el(
        25,
        'Product/Service ID Qualifier',
        'What kind of item identifier follows, for this sub-line.',
        {dataType: 'code', codes: PRODUCT_SERVICE_ID_QUALIFIER_CODES}
      ),
      el(
        27,
        'Product/Service ID Qualifier',
        'What kind of item identifier follows, for this sub-line.',
        {dataType: 'code', codes: PRODUCT_SERVICE_ID_QUALIFIER_CODES}
      ),
    ],
  },

  SCH: {
    id: 'SCH',
    name: 'Line Item Schedule',
    purpose:
      'Splits the line quantity across multiple requested dates — a delivery schedule.',
    loop: LoopId.Item,
    elements: [
      el(1, 'Quantity', 'The quantity for this scheduled date.', {
        dataType: 'numeric',
        required: true,
      }),
      el(
        2,
        'Unit or Basis for Measurement Code',
        'The unit the quantity is expressed in.',
        {dataType: 'code'}
      ),
      el(3, 'Date/Time Qualifier', 'What this date means.', {dataType: 'code'}),
      el(4, 'Date', 'The scheduled date.', {
        dataType: 'date',
        format: 'CCYYMMDD',
      }),
      el(5, 'Time', 'The scheduled time.', {dataType: 'time'}),
    ],
  },

  CSH: {
    id: 'CSH',
    name: 'Sales Requirements',
    purpose:
      'States a special sales condition for the line, e.g. that back orders are or are not allowed.',
    loop: LoopId.Item,
    elements: [
      el(1, 'Sales Requirement Code', 'The sales condition that applies.', {
        dataType: 'code',
      }),
      el(
        2,
        'Sales Requirement Description',
        'Free-form description of the condition.'
      ),
    ],
  },

  TC2: {
    id: 'TC2',
    name: 'Commodity',
    purpose:
      'Classifies the item by a standard commodity code (e.g. for customs or freight rating).',
    loop: LoopId.Item,
    elements: [
      el(
        1,
        'Commodity Code Qualifier',
        'Which commodity code scheme is used.',
        {dataType: 'code'}
      ),
      el(2, 'Commodity Code', 'The commodity code value.'),
    ],
  },

  TC1: {
    id: 'TC1',
    name: 'Transportation Detail',
    purpose:
      "Describes how the line should ship. Opens the line's transportation sub-loop.",
    loop: LoopId.Transportation,
    elements: [
      el(
        1,
        'Transportation Method/Type Code',
        'How the shipment should move.',
        {
          dataType: 'code',
          codes: [
            {code: 'M', label: 'Motor (Common Carrier)'},
            {code: 'A', label: 'Air'},
            {code: 'LT', label: 'Less Than Trailer Load'},
          ],
        }
      ),
      el(
        2,
        'Transportation Carrier Code',
        'The carrier assigned to move this shipment.'
      ),
      el(
        3,
        'Carrier Identifier Code',
        'Identifies the carrier by a coded id (e.g. SCAC).'
      ),
    ],
  },

  CTT: {
    id: 'CTT',
    name: 'Transaction Totals',
    purpose:
      'States how many line items are in the transaction — a control total the receiver can check its own count against.',
    loop: LoopId.Summary,
    required: true,
    elements: [
      el(1, 'Number of Line Items', 'Total PO1 lines in this transaction.', {
        dataType: 'numeric',
        required: true,
      }),
      el(2, 'Hash Total', 'Optional hash total for an extra integrity check.', {
        dataType: 'numeric',
      }),
    ],
  },

  AMT: {
    id: 'AMT',
    name: 'Monetary Amount',
    purpose:
      'States a total monetary amount for the transaction, e.g. the order total.',
    loop: LoopId.Summary,
    elements: [
      el(1, 'Amount Qualifier Code', 'What this amount represents.', {
        dataType: 'code',
        required: true,
        codes: [
          {code: 'TT', label: 'Total'},
          {code: 'DA', label: 'Discount Amount'},
          {code: 'ZZ', label: 'Mutually Defined'},
        ],
      }),
      el(2, 'Monetary Amount', 'The amount value.', {
        dataType: 'numeric',
        required: true,
      }),
    ],
  },
};
